/**
 * Project Settings -> "Tree and shrub crowns": how the project's Tree / Shrub
 * polygons are read as individual crowns for the fire graph and the 3D fuel
 * layer. Sizes are shown as widths (a tree is "15 m across", nobody thinks in
 * radii) in the user's unit system; the backend stores metres and radii.
 *
 * Controlled: the parent holds the overrides (metres, only the keys that
 * differ from the defaults) and saves them with the rest of the tab.
 */
import { useEffect, useRef, useState } from 'react';
import { Box, Button, Collapse, FormHelperText, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import {
  CROWN_SETTING_DEFAULTS,
  CrownSettingKey,
  CrownSettingsOverrides,
} from '../../api/crowns';
import type { UnitSystem } from '../../utils/units';

const M_TO_FEET = 3.28084;

type Measure = 'width' | 'length';

interface FieldSpec {
  key: CrownSettingKey;
  label: string;
  help: string;
  /** `width` fields store a radius and show a diameter. */
  measure: Measure;
}

const TREE_FIELDS: FieldSpec[] = [
  {
    key: 'r_clump_tree_m',
    label: 'Largest single tree',
    measure: 'width',
    help: 'Tree patches up to this width across count as one tree with its own size. Wider patches are treated as a clump of trees.',
  },
  {
    key: 'spacing_tree_m',
    label: 'Tree spacing in clumps',
    measure: 'length',
    help: 'Distance between trees inside a clump or along a row. Crowns in a clump are drawn a little wider than half this.',
  },
  {
    key: 'height_clump_tree_m',
    label: 'Tree height in clumps',
    measure: 'length',
    help: 'Height of the trees inside a clump. Single trees take their height from their width.',
  },
];

const SHRUB_FIELDS: FieldSpec[] = [
  {
    key: 'r_clump_shrub_m',
    label: 'Largest single shrub',
    measure: 'width',
    help: 'Shrub patches up to this width across count as one shrub. Wider patches are treated as a clump.',
  },
  {
    key: 'spacing_shrub_m',
    label: 'Shrub spacing in clumps',
    measure: 'length',
    help: 'Distance between shrubs inside a clump or along a hedge.',
  },
  {
    key: 'height_single_shrub_m',
    label: 'Single shrub height',
    measure: 'length',
    help: 'Height of a shrub that stands on its own.',
  },
  {
    key: 'height_clump_shrub_m',
    label: 'Shrub height in clumps',
    measure: 'length',
    help: 'Height of the shrubs inside a clump or hedge.',
  },
];

const unitLabel = (unitSystem: UnitSystem) => (unitSystem === 'imperial' ? 'ft' : 'm');

/** Stored metres (a radius for widths) -> the number shown in the field. */
const toDisplay = (metres: number, measure: Measure, unitSystem: UnitSystem): number => {
  const m = measure === 'width' ? metres * 2 : metres;
  const shown = unitSystem === 'imperial' ? m * M_TO_FEET : m;
  return Math.round(shown * 10) / 10;
};

/** A number typed in the field -> stored metres (a radius for widths). */
const fromDisplay = (shown: number, measure: Measure, unitSystem: UnitSystem): number => {
  const m = unitSystem === 'imperial' ? shown / M_TO_FEET : shown;
  const metres = measure === 'width' ? m / 2 : m;
  return Math.round(metres * 100) / 100;
};

const formatNumber = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

const textFor = (
  value: CrownSettingsOverrides,
  unitSystem: UnitSystem,
): Record<CrownSettingKey, string> => {
  const out = {} as Record<CrownSettingKey, string>;
  for (const spec of [...TREE_FIELDS, ...SHRUB_FIELDS]) {
    const metres = value[spec.key] ?? CROWN_SETTING_DEFAULTS[spec.key];
    out[spec.key] = formatNumber(toDisplay(metres, spec.measure, unitSystem));
  }
  return out;
};

const fieldSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
    '&.Mui-focused fieldset': { borderColor: '#00d4ad' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
  '& .MuiInputBase-input': { color: '#fff' },
  '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' },
};

interface CrownSettingsSectionProps {
  value: CrownSettingsOverrides;
  onChange: (next: CrownSettingsOverrides) => void;
  unitSystem: UnitSystem;
}

export default function CrownSettingsSection({ value, onChange, unitSystem }: CrownSettingsSectionProps) {
  const [text, setText] = useState<Record<CrownSettingKey, string>>(() => textFor(value, unitSystem));
  const [shrubsOpen, setShrubsOpen] = useState(
    () => SHRUB_FIELDS.some((f) => value[f.key] !== undefined),
  );
  // Re-derive the field text only when the value arrives from outside (project
  // load, reset), never on the echo of our own onChange, so typing "1." is
  // not reformatted to "1" under the cursor.
  const lastEmitted = useRef<CrownSettingsOverrides | null>(null);
  useEffect(() => {
    if (value === lastEmitted.current) return;
    setText(textFor(value, unitSystem));
  }, [value, unitSystem]);

  const emit = (next: CrownSettingsOverrides) => {
    lastEmitted.current = next;
    onChange(next);
  };

  const handleField = (spec: FieldSpec, raw: string) => {
    setText((prev) => ({ ...prev, [spec.key]: raw }));
    const shown = Number(raw);
    const next: CrownSettingsOverrides = { ...value };
    if (!raw.trim() || !Number.isFinite(shown) || shown <= 0) {
      // Not a usable number yet: leave the stored value alone.
      return;
    }
    const metres = fromDisplay(shown, spec.measure, unitSystem);
    if (Math.abs(metres - CROWN_SETTING_DEFAULTS[spec.key]) < 0.005) delete next[spec.key];
    else next[spec.key] = metres;
    emit(next);
  };

  const reset = () => {
    const next: CrownSettingsOverrides = {};
    emit(next);
    setText(textFor(next, unitSystem));
  };

  const isDefault = Object.keys(value).length === 0;
  const unit = unitLabel(unitSystem);

  const renderField = (spec: FieldSpec) => {
    const raw = text[spec.key];
    const shown = Number(raw);
    const invalid = raw.trim() !== '' && (!Number.isFinite(shown) || shown <= 0);
    const overridden = value[spec.key] !== undefined;
    return (
      <TextField
        key={spec.key}
        label={`${spec.label} (${unit})`}
        value={raw}
        onChange={(e) => handleField(spec, e.target.value)}
        type="number"
        inputProps={{ min: 0, step: unitSystem === 'imperial' ? 1 : 0.5 }}
        fullWidth
        variant="outlined"
        error={invalid}
        helperText={
          invalid
            ? 'Enter a number above zero.'
            : `${spec.help}${
                overridden
                  ? ` Default ${formatNumber(
                      toDisplay(CROWN_SETTING_DEFAULTS[spec.key], spec.measure, unitSystem),
                    )} ${unit}.`
                  : ''
              }`
        }
        sx={fieldSx}
      />
    );
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 500, color: '#fff' }}>
          Tree and shrub crowns
        </Typography>
        <Button
          size="small"
          onClick={reset}
          disabled={isDefault}
          sx={{ color: 'rgba(255, 255, 255, 0.7)', textTransform: 'none' }}
        >
          Reset to defaults
        </Button>
      </Box>
      <FormHelperText sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 2, ml: 0 }}>
        Tree and shrub areas mapped in the vegetation editor are turned into
        individual crowns: these settings decide where one tree ends and a
        clump begins, and how clumps are filled. The crowns are drawn in the
        3D fuel layer and can catch and throw fire in urban simulations.
        Saving rebuilds the crowns in the background; the 3D layer updates
        when they are ready.
      </FormHelperText>

      {TREE_FIELDS.map(renderField)}

      <Button
        size="small"
        onClick={() => setShrubsOpen((o) => !o)}
        endIcon={shrubsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ color: 'rgba(255, 255, 255, 0.7)', textTransform: 'none', mb: 1, ml: -1 }}
      >
        Shrub settings
      </Button>
      <Collapse in={shrubsOpen}>
        <Box sx={{ pt: 1 }}>{SHRUB_FIELDS.map(renderField)}</Box>
      </Collapse>
    </Box>
  );
}
